import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import {
  IconGraphy,
  Typography,
  colors,
  Button,
  Table,
  Badge,
  Dropdown,
  Loading,
} from '@leapeasy/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { openDetails } from 'store/actions/uiActions';
import { deleteEmailAction, getEmailDetailAction } from 'store/actions/emailActions';
import { inviteUserAction } from 'store/actions/inviteActions';
import { getBuildingDetailAction } from 'store/actions/propertyActions';
import { failedNotif, successNotif } from 'store/actions/notifActions';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { InvitationConfirmDialog } from 'components/Email/InvitationConfirmDialog';
import { DetailEmailCard } from './DetailEmailCard';
import { useListQueryState } from 'hooks/useListQueryState';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import { useDemoMask } from 'hooks/useDemoMask';
import { buildMaskedEmailDetailBuildingRow } from 'utils/demoMask';
import { DetailPageShell } from 'components/DetailPageState';
import { useDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { fitContentTextColumnOptions } from 'utils/fitContentTextColumnOptions';
import { useBuildingListFetch } from 'hooks/useBuildingListFetch';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import { useLazyExportDatasetTotal } from 'hooks/useLazyExportDatasetTotal';
import { getBuilding } from 'store/sagas/APIs/user';
import { fetchAllPaginated } from 'utils/exportPagination';
import {
  buildPropertyListFilterChips,
  parsePropertyListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import { BOOLEAN_YES_NO_OPTIONS, formatBooleanYesNo } from 'utils/formatBooleanYesNo';
import {
  EMPTY_INVITATION_DETAIL_BUILDING_FILTER,
  INVITATION_DETAIL_BUILDING_SEARCH_PLACEHOLDER,
  areInvitationDetailFilterArraysEqual,
  parseInvitationDetailBuildingFilters,
  serializeInvitationDetailBuildingFilters,
} from 'utils/invitationDetailBuildingQueryFilters';
import {
  INVITATION_DETAIL_BUILDING_DEFAULT_VISIBLE_KEYS,
  INVITATION_DETAIL_BUILDING_STORAGE_KEY,
} from './emailDetailBuildingTableConfig';

const RESENDABLE_STATUSES = ['expired', 'pending', 'declined'];

const canResendInvitation = (status) => RESENDABLE_STATUSES.includes(status);

const mapInvitationBuildingExportRow = (building, isDemo) =>
  buildMaskedEmailDetailBuildingRow(building, isDemo);

export const EmailDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { emailId } = useParams();
  const { pathname } = useLocation();

  const { isDemo } = useDemoMask();
  const [tableData, setTableData] = useState([]);
  const buildingTableRef = useRef(null);
  const [buildingExportOpen, setBuildingExportOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [awaitingDelete, setAwaitingDelete] = useState(false);
  const [awaitingResend, setAwaitingResend] = useState(false);
  const actionLoading = useSelector((state) => state.getIn(['auth', 'loading']));
  const { hasSuccess: hasActionSuccess, hasError: hasActionError } = useNotificationMessages();
  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: INVITATION_DETAIL_BUILDING_SEARCH_PLACEHOLDER,
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: {
      selectedBType: [],
      isSHouse: [],
    },
    parseExtraFilters: parseInvitationDetailBuildingFilters,
    serializeExtraFilters: serializeInvitationDetailBuildingFilters,
  });

  const [selectedBType, selSelectedBType] = useState([]);
  const [isSHouse, setIsSHouse] = useState([]);
  const [selectedBTypeDraft, setSelectedBTypeDraft] = useState([]);
  const [isSHouseDraft, setIsSHouseDraft] = useState([]);

  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsType = details ? details.get('type') : null;
  const detailsData = details ? details.get('data') : null;
  const emailDetail = useSelector((state) => state.getIn(['email', 'detail'])) || null;
  const buildings = useSelector((state) => state.getIn(['property', 'building']));
  const buildingsJSON = useImmutableJs(buildings);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedBType = Array.isArray(extraFilters.selectedBType)
      ? extraFilters.selectedBType
      : [];
    const nextIsSHouse = Array.isArray(extraFilters.isSHouse) ? extraFilters.isSHouse : [];
    selSelectedBType((prev) =>
      areInvitationDetailFilterArraysEqual(prev, nextSelectedBType) ? prev : nextSelectedBType,
    );
    setIsSHouse((prev) =>
      areInvitationDetailFilterArraysEqual(prev, nextIsSHouse) ? prev : nextIsSHouse,
    );
    setSelectedBTypeDraft((prev) =>
      areInvitationDetailFilterArraysEqual(prev, nextSelectedBType) ? prev : nextSelectedBType,
    );
    setIsSHouseDraft((prev) =>
      areInvitationDetailFilterArraysEqual(prev, nextIsSHouse) ? prev : nextIsSHouse,
    );
  }, [extraFilters]);

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    navigate('/invitations');
  }, [dispatch, navigate]);

  const goDetailPage = (tableMeta) => {
    const buildingIdFromRow = Array.isArray(tableMeta)
      ? tableMeta[tableMeta.length - 1]
      : tableMeta;
    const selectedBuilding = (buildingsJSON?.data || []).find(
      (building) => building.id === buildingIdFromRow || building.building_id === buildingIdFromRow,
    );
    const buildingId = selectedBuilding?.building_id;
    if (!selectedBuilding || !buildingId) {
      dispatch(failedNotif('Property detail ID is missing.'));
      return;
    }

    dispatch(
      openDetails({
        type: 'property',
        data: selectedBuilding,
      }),
    );
    dispatch(getBuildingDetailAction(buildingId));
    const detailBase = pathname.startsWith('/email/')
      ? `/email/${emailId}/detail`
      : `/invitations/${emailId}/detail`;
    navigate(`${detailBase}/propertyDetail/${buildingId}/report`);
  };

  useEffect(() => {
    if (!emailId) return;
    dispatch(getEmailDetailAction(emailId));
  }, [dispatch, emailId]);

  useEffect(() => {
    if (emailId) return;
    if (!details || detailsType !== 'email') {
      dispatch(failedNotif('Email detail ID is missing.'));
      onClickBack();
    }
  }, [emailId, details, detailsType, dispatch, onClickBack]);

  useEffect(() => {
    if (!awaitingDelete || !hasActionSuccess) return;
    setDeleteConfirmOpen(false);
    setAwaitingDelete(false);
    onClickBack();
  }, [awaitingDelete, hasActionSuccess, onClickBack]);

  useEffect(() => {
    if (!awaitingResend || !hasActionSuccess) return;
    setResendConfirmOpen(false);
    setAwaitingResend(false);
    if (emailId) {
      dispatch(getEmailDetailAction(emailId));
    }
  }, [awaitingResend, hasActionSuccess, dispatch, emailId]);

  useEffect(() => {
    if (hasActionError) {
      setAwaitingDelete(false);
      setAwaitingResend(false);
    }
  }, [hasActionError]);

  const emailDetailJSON = useImmutableJs(emailDetail);
  const detailsDataJSON = useImmutableJs(detailsData);
  const hasUiFallback = !emailId && detailsType === 'email' && detailsData;
  const {
    data: email,
    isLoading: detailLoading,
    hasError: detailHasError,
  } = useDetailPageState({
    routeId: emailId,
    detailRecord: emailDetailJSON,
    hasUiFallback,
    fallbackData: detailsDataJSON,
  });

  const propertyId = email?.property;
  const hasProperties = Boolean(propertyId?.length);

  useEffect(() => {
    if (!hasProperties) {
      setTableData([]);
      return;
    }
    setTableData(
      (buildingsJSON?.data || []).map((building) =>
        buildMaskedEmailDetailBuildingRow(building, isDemo),
      ),
    );
  }, [hasProperties, buildingsJSON, isDemo]);

  useBuildingListFetch({
    propertyId,
    filter,
    pagination,
    sortOptions,
    selectedBType,
    isSHouse,
    enabled: hasProperties && !detailLoading && !detailHasError,
  });

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: hasProperties ? Number(buildingsJSON?.total) || 0 : 0,
    isLoading: hasProperties && Boolean(buildingsJSON?.isLoading),
  });

  const onRetry = useCallback(() => {
    if (emailId) dispatch(getEmailDetailAction(emailId));
  }, [emailId, dispatch]);

  const handleResend = useCallback(() => {
    if (!email?.id) return;
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingResend(true);
    dispatch(
      inviteUserAction({
        emailSubject: "You've been Invited to the Leap Portal",
        type: 'resend-email',
        id: email.id,
      }),
    );
  }, [dispatch, email?.id]);

  const handleDelete = useCallback(() => {
    if (!email?.id) return;
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingDelete(true);
    dispatch(deleteEmailAction({ id: email.id }));
  }, [dispatch, email?.id]);

  const fetchInvitationBuildingExportTotal = useCallback(async () => {
    if (!hasProperties) return undefined;
    const response = await getBuilding({
      id: propertyId,
      filter: EMPTY_INVITATION_DETAIL_BUILDING_FILTER,
      offset: 0,
      limit: 1,
      sortOptions,
      selectedBType: [],
      isSHouse: [],
    });
    return response?.total != null ? Number(response.total) : undefined;
  }, [hasProperties, propertyId, sortOptions]);

  const { fullExportDatasetTotal, isLoadingFullExportTotal, loadFullExportDatasetTotal } =
    useLazyExportDatasetTotal(fetchInvitationBuildingExportTotal);

  const handleRequestExportData = useCallback(async () => {
    if (!hasProperties) return [];
    const totalRecords = Number(buildingsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    const rows = await fetchAllPaginated({
      totalRecords,
      fetchPage: ({ offset, limit }) =>
        getBuilding({
          id: propertyId,
          filter,
          offset,
          limit,
          sortOptions,
          selectedBType,
          isSHouse,
        }),
    });
    return rows.map((building) => mapInvitationBuildingExportRow(building, isDemo));
  }, [
    hasProperties,
    propertyId,
    buildingsJSON?.total,
    filter,
    sortOptions,
    selectedBType,
    isSHouse,
    isDemo,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    if (!hasProperties) return [];
    const totalRecords = Number(fullExportDatasetTotal) || Number(buildingsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    const rows = await fetchAllPaginated({
      totalRecords,
      fetchPage: ({ offset, limit }) =>
        getBuilding({
          id: propertyId,
          filter: EMPTY_INVITATION_DETAIL_BUILDING_FILTER,
          offset,
          limit,
          sortOptions,
          selectedBType: [],
          isSHouse: [],
        }),
    });
    return rows.map((building) => mapInvitationBuildingExportRow(building, isDemo));
  }, [
    hasProperties,
    propertyId,
    fullExportDatasetTotal,
    buildingsJSON?.total,
    sortOptions,
    isDemo,
  ]);

  const resetFunc = useCallback(() => {
    selSelectedBType([]);
    setIsSHouse([]);
    setSelectedBTypeDraft([]);
    setIsSHouseDraft([]);
    setExtraFilters({
      selectedBType: [],
      isSHouse: [],
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const applyFunc = useCallback(() => {
    selSelectedBType(selectedBTypeDraft);
    setIsSHouse(isSHouseDraft);
    setExtraFilters({
      selectedBType: selectedBTypeDraft,
      isSHouse: isSHouseDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [selectedBTypeDraft, isSHouseDraft, setExtraFilters, setPagination]);

  const listFilterChips = useMemo(
    () =>
      buildPropertyListFilterChips({
        selectedBType,
        isSHouse,
        unitFilter: null,
      }),
    [selectedBType, isSHouse],
  );

  const handleRemoveBuildingFilterChip = useCallback(
    (chipId) => {
      const parsed = parsePropertyListFilterChipId(chipId);
      if (!parsed) return;
      if (parsed.type === 'propertyType') {
        const next = selectedBType.filter((value) => value !== parsed.value);
        selSelectedBType(next);
        setSelectedBTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedBType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }
      if (parsed.type === 'studentHousing') {
        const next = isSHouse.filter((value) => value !== parsed.value);
        setIsSHouse(next);
        setIsSHouseDraft(next);
        setExtraFilters((prev) => ({ ...prev, isSHouse: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [selectedBType, isSHouse, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveBuildingFilterChip,
    resetFunc,
  );

  const resendEnabled = email ? canResendInvitation(email.request_status) : false;
  const totalItems = hasProperties ? Number(buildingsJSON?.total) || 0 : 0;
  const buildingsLoading = hasProperties && Boolean(buildingsJSON?.isLoading);
  const hasActiveListQuery =
    Boolean(`${filter?.searchText || ''}`.trim()) || listFilterChips.length > 0;

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      {detailLoading || detailHasError ? (
        <DetailPageShell
          onBack={onClickBack}
          isLoading={detailLoading}
          hasError={detailHasError}
          onRetry={onRetry}
          loadingLabel="Loading invitation details..."
          skeletonVariant="fields"
        />
      ) : email ? (
        <>
          <Box
            padding="24px"
            gap="10px"
            display="flex"
            flexDirection="column"
            border="1px solid #EAEAEA"
            sx={{ background: 'white' }}
            borderRadius="12px"
          >
            <Box
              display={'flex'}
              alignItems={'center'}
              mb={'15px'}
              justifyContent={'space-between'}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ cursor: 'pointer', '&:hover': { opacity: 0.75 } }}>
                  <IconGraphy icon="Arrow.ArrowBack" onClick={onClickBack} />
                </Box>
                <Typography
                  variant="h3"
                  style={{
                    color: colors.purple[900],
                    fontWeight: '500',
                    padding: '5px 5px 5px 15px',
                  }}
                >
                  {email.requestor?.username || email.requestor_email || 'Invitation'}
                </Typography>
              </Box>

              <Box display={'flex'}>
                <InvitationConfirmDialog
                  type="resend"
                  open={resendConfirmOpen}
                  onClose={() => {
                    if (awaitingResend) return;
                    setResendConfirmOpen(false);
                  }}
                  onConfirm={handleResend}
                  isSubmitting={actionLoading && awaitingResend}
                />
                <InvitationConfirmDialog
                  type="delete"
                  open={deleteConfirmOpen}
                  onClose={() => {
                    if (awaitingDelete) return;
                    setDeleteConfirmOpen(false);
                  }}
                  onConfirm={handleDelete}
                  isSubmitting={actionLoading && awaitingDelete}
                />
                <Button
                  iconPrefix="General.Delete"
                  onClick={() => setDeleteConfirmOpen(true)}
                  size="medium"
                  style={{
                    width: '160px',
                    marginRight: '15px',
                  }}
                  variant="secondary"
                >
                  Delete Email
                </Button>
                <Button
                  iconPrefix="EditorLayout.Send"
                  onClick={() => setResendConfirmOpen(true)}
                  disabled={!resendEnabled}
                  size="medium"
                  style={{
                    width: '160px',
                  }}
                >
                  Resend
                </Button>
              </Box>
            </Box>
            <DetailEmailCard email={email} />
          </Box>
          <Box
            padding="24px"
            gap="10px"
            display="flex"
            flexDirection="column"
            border="1px solid #EAEAEA"
            sx={{ background: 'white' }}
            borderRadius="12px"
            marginTop="20px"
          >
            <CustomBox>
              {buildingsLoading && (
                <LoadingEffect>
                  <Loading size={'large'} />
                </LoadingEffect>
              )}
              <Table
                showEmptyState={!buildingsLoading}
                emptyContent={
                  <GlobalUXState
                    variant={hasActiveListQuery ? 'noResults' : 'noData'}
                    compact
                    description={
                      hasActiveListQuery
                        ? 'No properties match your search or filters in this invitation.'
                        : 'No properties are attached to this invitation.'
                    }
                  />
                }
                ref={buildingTableRef}
                onExportButtonClick={() => setBuildingExportOpen(true)}
                onRequestExportData={hasProperties ? handleRequestExportData : undefined}
                onRequestFullExportData={hasProperties ? handleRequestFullExportData : undefined}
                columns={[
                  {
                    name: 'Name',
                    options: {
                      ...fitContentTextColumnOptions(),
                      sort: true,
                    },
                    key: 'name',
                  },
                  {
                    name: 'Property Type',
                    options: {
                      flex: '30px 1 1',
                      filter: true,
                      customBodyRenderer: (value) => (
                        <Badge
                          background="rgba(243, 241, 244, 1)"
                          color={value ? 'purple' : 'tomato'}
                          label={value}
                          rounded
                          textSize="medium"
                        />
                      ),
                      sort: true,
                    },
                    key: 'building_type',
                  },
                  {
                    name: 'Phone Number',
                    options: {
                      sort: true,
                    },
                    key: 'phone',
                  },
                  {
                    name: 'Address',
                    options: {
                      flex: '210px 1 1',
                      sort: true,
                    },
                    key: 'billingStreet',
                  },
                  {
                    name: 'Email Address',
                    options: {
                      flex: '210px 1 1',
                      sort: true,
                    },
                    key: 'email_address',
                  },
                  {
                    name: 'Unit Count',
                    options: {
                      filter: false,
                      sort: true,
                      customBodyRender: (value) => (
                        <Typography align="center" variant="body1">
                          {value != null ? Number(value).toLocaleString('en-US') : ''}
                        </Typography>
                      ),
                    },
                    key: 'total_of_units',
                  },
                  {
                    name: 'Student Housing',
                    options: {
                      flex: '40px 1 1',
                      filter: true,
                      sort: true,
                      customBodyRenderer: (value) => (
                        <Badge
                          background="#F3F1F4"
                          color={value === 'true' ? 'parisGreen' : 'tomato'}
                          label={formatBooleanYesNo(value)}
                          rounded
                          textSize="medium"
                        />
                      ),
                      filterOptions: BOOLEAN_YES_NO_OPTIONS,
                    },
                    key: 'student_housing',
                  },
                  {
                    name: 'Detail',
                    options: {
                      flex: '5px 1 1',
                      sort: true,
                      customBodyRenderer: (value, tableMeta) => (
                        <IconGraphy
                          icon={'FileFolder.Description'}
                          style={{ color: '#702572' }}
                          onClick={() => goDetailPage(tableMeta)}
                        />
                      ),
                    },
                    key: 'detail',
                  },
                ]}
                data={tableData}
                filter={filter}
                onChangeFilter={setFilter}
                defaultVisibleColumnKeys={INVITATION_DETAIL_BUILDING_DEFAULT_VISIBLE_KEYS}
                columnVisibilityStorageKey={INVITATION_DETAIL_BUILDING_STORAGE_KEY}
                autoTooltipForDefaultCells
                dropFilterActiveSummary={dropFilterActiveSummary}
                dropFilterActiveCount={dropFilterActiveCount}
                dropFilter={{
                  component: (
                    <DropFilterContainer>
                      <Dropdown
                        onChange={(e) => {
                          setSelectedBTypeDraft(e.target.value);
                        }}
                        options={[
                          {
                            text: 'Guarantor Waiver+LDR',
                            value: 'Event Process;Auto Enroll',
                          },
                          {
                            text: 'Rent Guaranty',
                            value: 'Auto Enroll',
                          },
                        ]}
                        value={selectedBTypeDraft}
                        multiselect={true}
                        showSearch={false}
                        placeholder="Property Type"
                        label="Property Type"
                        style={{
                          width: '100%',
                        }}
                      />
                      <Dropdown
                        onChange={(e) => {
                          setIsSHouseDraft(e.target.value);
                        }}
                        options={BOOLEAN_YES_NO_OPTIONS}
                        value={isSHouseDraft}
                        multiselect={true}
                        showSearch={false}
                        placeholder="Student Housing"
                        label="Student Housing"
                        style={{
                          width: '100%',
                        }}
                      />
                    </DropFilterContainer>
                  ),
                  resetFunc,
                  applyFunc,
                }}
                onChangeRowsPerPage={(value) =>
                  setPagination({
                    ...pagination,
                    rowsPerPage: value,
                    pageNumber: 0,
                  })
                }
                pagination={{
                  ...pagination,
                  totalItems,
                  totalPages: Math.ceil(totalItems / pagination.rowsPerPage) || 0,
                }}
                sortOptions={sortOptions}
                onChangeSort={setSortOptions}
                style={{ width: '100%' }}
                title="Properties"
              />
            </CustomBox>
            <ListExportScopeModal
              open={buildingExportOpen}
              onClose={() => setBuildingExportOpen(false)}
              tableRef={buildingTableRef}
              title="Properties"
              currentPageRowCount={tableData.length}
              paginationTotalItems={totalItems}
              fullDatasetTotal={fullExportDatasetTotal}
              onLoadFullDatasetTotal={hasProperties ? loadFullExportDatasetTotal : undefined}
              isLoadingFullDatasetTotal={isLoadingFullExportTotal}
              enableFiltered={hasProperties}
              enableFull={hasProperties}
              enableSelected={false}
            />
          </Box>
        </>
      ) : null}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;
